import { useEffect, useMemo, useRef, type ClipboardEvent } from 'react';
import { ImageSquare, Paperclip, X } from '@phosphor-icons/react';
import { formatBytes } from './AdjuntosMensaje';
import styles from './Adjuntar.module.css';

/**
 * Adjuntar a un mensaje o a un alta (paso 34, K6 y K25).
 *
 * Los ficheros NO se suben al elegirlos: se quedan aquí, en chips que se
 * pueden quitar, y viajan con el mensaje en el MISMO envío. Así o se guarda
 * todo (mensaje, adjuntos y tiempo) o nada, y nunca queda un fichero subido
 * de un mensaje que no se llegó a mandar.
 *
 * Qué se acepta lo decide el backend (`attachments.py::clasificar`): capturas
 * PNG/JPEG comprobadas por su firma, y cualquier fichero salvo ejecutables y
 * scripts, con el tope de tamaño de Parámetros. Aquí solo se cuenta, para
 * avisar antes de enviar; el resto lo dice el error del servidor, con el
 * nombre del fichero.
 */

/** Añade `nuevos` a `actuales` hasta `max`. Devuelve la lista y, si sobraron,
 *  el aviso para la persona. */
export function sumarAdjuntos(
  actuales: File[],
  nuevos: File[],
  max: number,
): { archivos: File[]; aviso: string | null } {
  const caben = Math.max(0, max - actuales.length);
  const archivos = [...actuales, ...nuevos.slice(0, caben)];
  const sobran = nuevos.length - Math.min(caben, nuevos.length);
  return {
    archivos,
    aviso: sobran > 0 ? `Como mucho ${max} adjuntos por mensaje: ${sobran} no se han añadido.` : null,
  };
}

/** Las capturas pegadas con Ctrl+V. Es como llega casi siempre un pantallazo:
 *  Impr Pant y pegar, sin pasar por un fichero. El portapapeles las llama
 *  todas «image.png», así que se les pone la hora para distinguirlas. */
export function capturasPegadas(e: ClipboardEvent): File[] {
  const items = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
  if (items.length === 0) return [];
  const hora = new Date().toTimeString().slice(0, 8).replace(/:/g, '');
  return items.map((f, i) => {
    const ext = f.type === 'image/jpeg' ? 'jpg' : 'png';
    const nombre = `captura-${hora}${items.length > 1 ? `-${i + 1}` : ''}.${ext}`;
    return new File([f], nombre, { type: f.type });
  });
}

interface BotonProps {
  onElegir: (archivos: File[]) => void;
  disabled?: boolean;
  /** Con texto al lado del clip (altas y portal) o solo el clip (compositor). */
  conTexto?: boolean;
}

export function BotonAdjuntar({ onElegir, disabled, conTexto }: BotonProps) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className={styles.boton}
        onClick={() => input.current?.click()}
        disabled={disabled}
        aria-label="Adjuntar ficheros o capturas"
        title="Adjuntar ficheros o capturas (también puedes pegar una captura con Ctrl+V)"
      >
        <Paperclip size={15} weight="regular" />
        {conTexto && 'Adjuntar'}
      </button>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const elegidos = Array.from(e.target.files ?? []);
          // Vaciarlo deja volver a elegir el mismo fichero tras quitarlo.
          e.target.value = '';
          if (elegidos.length) onElegir(elegidos);
        }}
      />
    </>
  );
}

interface PendientesProps {
  archivos: File[];
  onQuitar: (i: number) => void;
  aviso?: string | null;
}

/** Lo que va a salir con el mensaje, antes de enviarlo. */
export function AdjuntosPendientes({ archivos, onQuitar, aviso }: PendientesProps) {
  // Una miniatura local para las capturas: lo que se ve es lo que se manda.
  const urls = useMemo(
    () => archivos.map((f) => (f.type === 'image/png' || f.type === 'image/jpeg' ? URL.createObjectURL(f) : null)),
    [archivos],
  );
  useEffect(() => () => urls.forEach((u) => u && URL.revokeObjectURL(u)), [urls]);

  if (archivos.length === 0 && !aviso) return null;
  return (
    <>
      {archivos.length > 0 && (
        <div className={styles.pendientes} aria-label="Adjuntos que se enviarán">
          {archivos.map((f, i) => (
            <span key={`${f.name}-${i}`} className={styles.chip} title={`${f.name} · ${formatBytes(f.size)}`}>
              {urls[i]
                ? <img src={urls[i]!} alt="" className={styles.chipThumb} />
                : f.type.startsWith('image/')
                  ? <ImageSquare size={13} weight="regular" />
                  : <Paperclip size={13} weight="regular" />}
              <span className={styles.chipName}>{f.name}</span>
              <span className={styles.chipSize}>{formatBytes(f.size)}</span>
              <button
                type="button"
                className={styles.quitar}
                onClick={() => onQuitar(i)}
                aria-label={`Quitar ${f.name}`}
              >
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      {aviso && <p className={styles.aviso} role="status">{aviso}</p>}
    </>
  );
}
